import React, { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { Cloud, Shell, Coins, Star, Sparkles } from 'lucide-react';
import { cn } from '../lib/utils';

interface BackgroundItem {
  id: number;
  type: 'icon' | 'symbol';
  name: string;
  x: number;
  size: number;
  duration: number;
  delay: number;
  colorClass: string;
  glowClass: string;
  blurClass: string;
  opacityClass: string;
  depth: 'back' | 'mid' | 'front';
}

interface TwinkleStar {
  id: number;
  width: number;
  height: number;
  top: number;
  left: number;
  duration: number;
  delay: number;
}

// Custom thin glowing outline SVG Math Operators
const PlusIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className={className}>
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const MinusIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className={className}>
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const MultiplyIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className={className}>
    <line x1="6" y1="6" x2="18" y2="18" />
    <line x1="18" y1="6" x2="6" y2="18" />
  </svg>
);

const DivideIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className={className}>
    <line x1="5" y1="12" x2="19" y2="12" />
    <circle cx="12" cy="5.5" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="12" cy="18.5" r="1.5" fill="currentColor" stroke="none" />
  </svg>
);

const PercentIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className={className}>
    <line x1="19" y1="5" x2="5" y2="19" />
    <circle cx="7.5" cy="7.5" r="2.5" />
    <circle cx="16.5" cy="16.5" r="2.5" />
  </svg>
);

const EqualIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className={className}>
    <line x1="5" y1="9" x2="19" y2="9" />
    <line x1="5" y1="15" x2="19" y2="15" />
  </svg>
);

const SqrtIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M4 12h2l4 8 6-16h4" />
  </svg>
);

const PiIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M4 6h16M7 6v13M17 6v13M17 19a2 2 0 0 1-2-2" />
  </svg>
);

const SYMBOLS = ['+', '−', '×', '÷', '%', '=', '√', 'π'];

const getIconProps = (name: string) => {
  switch (name) {
    case 'cloud':
      return {
        colorClass: 'text-sky-400/28',
        glowClass: 'drop-shadow-[0_0_8px_rgba(56,189,248,0.45)]',
      };
    case 'shell':
      return {
        colorClass: 'text-amber-400/24',
        glowClass: 'drop-shadow-[0_0_8px_rgba(251,191,36,0.4)]',
      };
    case 'coin':
      return {
        colorClass: 'text-yellow-400/24',
        glowClass: 'drop-shadow-[0_0_8px_rgba(253,224,71,0.4)]',
      };
    case 'star':
      return {
        colorClass: 'text-violet-400/35',
        glowClass: 'drop-shadow-[0_0_10px_rgba(167,139,250,0.5)]',
      };
    case 'sparkle':
      return {
        colorClass: 'text-teal-400/28',
        glowClass: 'drop-shadow-[0_0_8px_rgba(45,212,191,0.4)]',
      };
    default:
      return {
        colorClass: 'text-slate-400/24',
        glowClass: 'drop-shadow-[0_0_6px_rgba(255,255,255,0.15)]',
      };
  }
};

export default function SceneBackground() {
  const [items, setItems] = useState<BackgroundItem[]>([]);
  const [stars, setStars] = useState<TwinkleStar[]>([]);

  useEffect(() => {
    // Generate icons representing all standard modes
    const iconsPool = ['cloud', 'shell', 'coin', 'star', 'sparkle', 'cloud', 'shell', 'coin', 'star'];
    const generatedIcons: BackgroundItem[] = iconsPool.map((name, i) => {
      const rand = Math.random();
      const depth = rand < 0.25 ? 'back' : rand < 0.75 ? 'mid' : 'front';
      const { colorClass, glowClass } = getIconProps(name);

      let size = 40;
      let duration = 30;
      let blurClass = 'blur-none';
      let opacityClass = 'opacity-100';

      if (depth === 'back') {
        size = 20 + Math.random() * 6; // 20px to 26px
        duration = 45 + Math.random() * 20; // 45s to 65s (slowest)
        blurClass = 'blur-[2.5px]';
        opacityClass = 'opacity-45';
      } else if (depth === 'mid') {
        size = 34 + Math.random() * 10; // 34px to 44px
        duration = 28 + Math.random() * 12; // 28s to 40s (normal)
        blurClass = 'blur-none';
        opacityClass = 'opacity-100';
      } else {
        size = 54 + Math.random() * 14; // 54px to 68px
        duration = 16 + Math.random() * 8; // 16s to 24s (fastest)
        blurClass = 'blur-[1.2px]';
        opacityClass = 'opacity-70';
      }

      return {
        id: i,
        type: 'icon',
        name,
        x: 5 + Math.random() * 90,
        size,
        duration,
        delay: Math.random() * -30, // Random starting position offset
        colorClass,
        glowClass,
        blurClass,
        opacityClass,
        depth
      };
    });

    // Generate mathematical operators styled matching mode themes
    const modeColors = ['cloud', 'shell', 'coin', 'star', 'sparkle'];
    const generatedSymbols: BackgroundItem[] = SYMBOLS.map((symbol, i) => {
      const rand = Math.random();
      const depth = rand < 0.3 ? 'back' : rand < 0.8 ? 'mid' : 'front';

      // Pick a random mode's glowing palette for this symbol
      const themeColor = modeColors[Math.floor(Math.random() * modeColors.length)];
      const { colorClass, glowClass } = getIconProps(themeColor);

      let size = 28;
      let duration = 25;
      let blurClass = 'blur-none';
      let opacityClass = 'opacity-100';

      if (depth === 'back') {
        size = 14 + Math.random() * 6; // 14px to 20px
        duration = 40 + Math.random() * 15;
        blurClass = 'blur-[2px]';
        opacityClass = 'opacity-45';
      } else if (depth === 'mid') {
        size = 24 + Math.random() * 6; // 24px to 30px
        duration = 24 + Math.random() * 10;
        blurClass = 'blur-none';
        opacityClass = 'opacity-100';
      } else {
        size = 42 + Math.random() * 10; // 42px to 52px
        duration = 14 + Math.random() * 6;
        blurClass = 'blur-[1px]';
        opacityClass = 'opacity-70';
      }

      return {
        id: i + iconsPool.length,
        type: 'symbol',
        name: symbol,
        x: 5 + Math.random() * 90,
        size,
        duration,
        delay: Math.random() * -30,
        colorClass,
        glowClass,
        blurClass,
        opacityClass,
        depth
      };
    });

    setItems([...generatedIcons, ...generatedSymbols]);

    // Generate background stars
    const generatedStars: TwinkleStar[] = Array.from({ length: 30 }).map((_, i) => ({
      id: i,
      width: Math.random() * 1.5 + 1,
      height: Math.random() * 1.5 + 1,
      top: Math.random() * 55, // Upper half
      left: Math.random() * 100,
      duration: 2.5 + Math.random() * 3.5,
      delay: Math.random() * 4,
    }));
    setStars(generatedStars);
  }, []);

  return (
    <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden bg-gradient-to-b from-[#0f172a] via-[#090d16] to-[#020617]">
      {/* Subtle Dot Grid pattern */}
      <div 
        className="absolute inset-0 opacity-[0.03] pointer-events-none z-0 mix-blend-overlay" 
        style={{ 
          backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)', 
          backgroundSize: '32px 32px' 
        }} 
      />

      {/* Twinkling Background Stars */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        {stars.map((star) => (
          <motion.div
            key={star.id}
            className="absolute bg-white rounded-full"
            style={{
              width: `${star.width}px`,
              height: `${star.height}px`,
              top: `${star.top}%`,
              left: `${star.left}%`,
            }}
            animate={{
              opacity: [0.1, 0.6, 0.1],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: star.duration,
              repeat: Infinity,
              ease: "easeInOut",
              delay: star.delay,
            }}
          />
        ))}
      </div>

      {/* Floating 3D Depth Layer Elements */}
      {items.map((el) => {
        let content;
        if (el.type === 'icon') {
          if (el.name === 'cloud') {
            content = <Cloud className={cn("w-full h-full", el.colorClass, el.glowClass)} />;
          } else if (el.name === 'shell') {
            content = <Shell className={cn("w-full h-full", el.colorClass, el.glowClass)} />;
          } else if (el.name === 'coin') {
            content = <Coins className={cn("w-full h-full", el.colorClass, el.glowClass)} />;
          } else if (el.name === 'star') {
            content = <Star className={cn("w-full h-full", el.colorClass, el.glowClass)} />;
          } else if (el.name === 'sparkle') {
            content = <Sparkles className={cn("w-full h-full", el.colorClass, el.glowClass)} />;
          }
        } else {
          if (el.name === '+') {
            content = <PlusIcon className={cn("w-full h-full", el.colorClass, el.glowClass)} />;
          } else if (el.name === '−') {
            content = <MinusIcon className={cn("w-full h-full", el.colorClass, el.glowClass)} />;
          } else if (el.name === '×') {
            content = <MultiplyIcon className={cn("w-full h-full", el.colorClass, el.glowClass)} />;
          } else if (el.name === '÷') {
            content = <DivideIcon className={cn("w-full h-full", el.colorClass, el.glowClass)} />;
          } else if (el.name === '%') {
            content = <PercentIcon className={cn("w-full h-full", el.colorClass, el.glowClass)} />;
          } else if (el.name === '=') {
            content = <EqualIcon className={cn("w-full h-full", el.colorClass, el.glowClass)} />;
          } else if (el.name === '√') {
            content = <SqrtIcon className={cn("w-full h-full", el.colorClass, el.glowClass)} />;
          } else if (el.name === 'π') {
            content = <PiIcon className={cn("w-full h-full", el.colorClass, el.glowClass)} />;
          }
        }

        return (
          <motion.div
            key={el.id}
            className={cn("absolute pointer-events-none select-none", el.blurClass, el.opacityClass)}
            style={{ 
              width: `${el.size}px`, 
              height: `${el.size}px`,
              left: `${el.x}%` 
            }}
            initial={{ y: '110vh', rotate: 0 }}
            animate={{ y: '-15vh', rotate: 360 }}
            transition={{
              duration: el.duration,
              repeat: Infinity,
              ease: "linear",
              delay: el.delay,
            }}
          >
            {content}
          </motion.div>
        );
      })}

      {/* Ambient Central Nebula Glow */}
      <div className="absolute top-[45%] left-1/2 -translate-x-1/2 -translate-y-1/2 w-[280px] h-[280px] bg-gradient-to-tr from-violet-600/10 via-cyan-500/8 to-transparent rounded-full blur-[60px] pointer-events-none mix-blend-screen" />

      {/* Rotating Mathematical 3D Wireframe Icosahedron */}
      <motion.div 
        className="absolute top-[45%] left-1/2 pointer-events-none z-0 select-none mix-blend-screen"
        animate={{ 
          x: "-50%",
          y: "-50%",
          rotate: 360,
          scale: [0.95, 1.05, 0.95]
        }}
        transition={{ 
          rotate: { repeat: Infinity, duration: 25, ease: "linear" },
          scale: { repeat: Infinity, duration: 8, ease: "easeInOut" }
        }}
      >
        <svg viewBox="0 0 100 100" className="w-[180px] h-[180px] text-cyan-400/20 stroke-current fill-none" strokeWidth="0.8">
          {/* Outer pentagon outline */}
          <polygon points="50,10 88,38 73,82 27,82 12,38" strokeDasharray="2 1" />
          {/* Inner pentagon outline */}
          <polygon points="50,30 73,47 64,73 36,73 27,47" strokeDasharray="1 1" />
          {/* Connector lines from outer to inner */}
          <line x1="50" y1="10" x2="50" y2="30" />
          <line x1="88" y1="38" x2="73" y2="47" />
          <line x1="73" y1="82" x2="64" y2="73" />
          <line x1="27" y1="82" x2="36" y2="73" />
          <line x1="12" y1="38" x2="27" y2="47" />
          {/* Additional internal wireframe web */}
          <line x1="50" y1="30" x2="64" y2="73" />
          <line x1="50" y1="30" x2="36" y2="73" />
          <line x1="73" y1="47" x2="27" y2="47" />
          <line x1="73" y1="47" x2="36" y2="73" />
          <line x1="27" y1="47" x2="64" y2="73" />
          
          {/* Glowing vertices */}
          {/* Inner pentagon */}
          <circle cx="50" cy="30" r="1.5" className="fill-cyan-300/60 drop-shadow-[0_0_3px_rgba(34,211,238,0.8)] animate-pulse" />
          <circle cx="73" cy="47" r="1.5" className="fill-cyan-300/60 drop-shadow-[0_0_3px_rgba(34,211,238,0.8)] animate-pulse [animation-delay:0.2s]" />
          <circle cx="64" cy="73" r="1.5" className="fill-cyan-300/60 drop-shadow-[0_0_3px_rgba(34,211,238,0.8)] animate-pulse [animation-delay:0.4s]" />
          <circle cx="36" cy="73" r="1.5" className="fill-cyan-300/60 drop-shadow-[0_0_3px_rgba(34,211,238,0.8)] animate-pulse [animation-delay:0.6s]" />
          <circle cx="27" cy="47" r="1.5" className="fill-cyan-300/60 drop-shadow-[0_0_3px_rgba(34,211,238,0.8)] animate-pulse [animation-delay:0.8s]" />
          
          {/* Outer pentagon */}
          <circle cx="50" cy="10" r="1.5" className="fill-cyan-300/60 drop-shadow-[0_0_3px_rgba(34,211,238,0.8)] animate-pulse [animation-delay:1.0s]" />
          <circle cx="88" cy="38" r="1.5" className="fill-cyan-300/60 drop-shadow-[0_0_3px_rgba(34,211,238,0.8)] animate-pulse [animation-delay:1.2s]" />
          <circle cx="73" cy="82" r="1.5" className="fill-cyan-300/60 drop-shadow-[0_0_3px_rgba(34,211,238,0.8)] animate-pulse [animation-delay:1.4s]" />
          <circle cx="27" cy="82" r="1.5" className="fill-cyan-300/60 drop-shadow-[0_0_3px_rgba(34,211,238,0.8)] animate-pulse [animation-delay:1.6s]" />
          <circle cx="12" cy="38" r="1.5" className="fill-cyan-300/60 drop-shadow-[0_0_3px_rgba(34,211,238,0.8)] animate-pulse [animation-delay:1.8s]" />
        </svg>
      </motion.div>

      {/* Atmospheric vignette */}
      <div className="absolute inset-0 z-10 shadow-[inset_0_0_150px_rgba(2,6,23,0.96)] pointer-events-none" />
    </div>
  );
}
